/**
 * JavaScript port of the Python DotToCytoscapeConverter
 * Converts Graphviz DOT files to Cytoscape.js format using @hpcc-js/wasm
 * Based on the article: Bridging Graphviz and Cytoscape.js for Interactive Graphs
 */
import { Graphviz } from '@hpcc-js/wasm';

export class DotToCytoscapeConverter {
    constructor() {
        this.graphviz = null;
        this.nodePositions = {};
        this.isInitialized = false;
    }

    /**
     * Initialize the Graphviz WASM module
     */
    async initialize() {
        if (this.isInitialized) return;
        
        try {
            console.log("Initializing @hpcc-js/wasm Graphviz...");
            
            // Load the Graphviz module
            this.graphviz = await Graphviz.load();
            
            this.isInitialized = true;
            console.log("Graphviz WASM initialized successfully");
        } catch (error) {
            console.error("Failed to initialize Graphviz WASM:", error);
            throw new Error(`Failed to initialize Graphviz: ${error.message}`);
        }
    }

    /**
     * Takes the content of a normal graphviz DOT file. Returns JSON with layout information.
     * Uses Graphviz JSON output format for easier parsing.
     */
    renderDotToJson(dotString) {
        console.debug("Step 1: Creating Graphviz graph from DOT string...");
        
        if (!this.isInitialized) {
            throw new Error("Graphviz not initialized. Call initialize() first.");
        }

        console.debug("Step 2: Applying DOT layout algorithm...");

        try {
            // Render the DOT to JSON format with layout information
            const jsonOutput = this.graphviz.layout(dotString, "json", "dot");

            console.debug("Step 3: Parsing JSON output...");
            const graphData = JSON.parse(jsonOutput);

            console.debug("JSON Graph Data:", graphData);

            return graphData;
        } catch (error) {
            console.error("Error in Graphviz layout:", error);
            throw new Error(`Graphviz layout failed: ${error.message}`);
        }
    }

    /**
     * Parse JSON graph data from Graphviz and extract nodes and edges
     */
    parseJsonGraphData(jsonData) {
        console.debug("Step 4: Parsing JSON graph data...");

        const nodes = [];
        const edges = [];
        
        // Extract graph information from bounding box
        let graphInfo = {
            width: 100,
            height: 100,
            scale: 1.0
        };

        if (jsonData.bb) {
            const bbox = jsonData.bb.split(',').map(parseFloat);
            if (bbox.length >= 4) {
                graphInfo = {
                    width: bbox[2] - bbox[0],
                    height: bbox[3] - bbox[1],
                    scale: 1.0
                };
            }
        }

        // Process nodes
        if (jsonData.objects) {
            for (const obj of jsonData.objects) {
                if (obj._gvid !== undefined) { // This is a node
                    const node = {
                        id: obj.name || obj._gvid.toString(),
                        x: 0,
                        y: 0,
                        width: parseFloat(obj.width) || 1,
                        height: parseFloat(obj.height) || 1,
                        label: obj.label || obj.name || obj._gvid.toString(),
                        style: obj.style || '',
                        shape: obj.shape || 'box',
                        color: obj.color || '',
                        fillcolor: obj.fillcolor || obj.color || '',
                        fontcolor: obj.fontcolor || ''
                    };

                    // Parse position
                    if (obj.pos) {
                        const pos = obj.pos.split(',').map(parseFloat);
                        if (pos.length >= 2) {
                            node.x = pos[0];
                            node.y = pos[1];
                        }
                    }

                    nodes.push(node);
                }
            }
        }

        // Process edges
        if (jsonData.edges) {
            for (const edge of jsonData.edges) {
                // Find source and target node names
                let sourceName = edge.tail?.toString() || '';
                let targetName = edge.head?.toString() || '';

                // Try to match with actual node names if we have _gvid references
                if (jsonData.objects) {
                    const sourceObj = jsonData.objects.find(obj => obj._gvid === edge.tail);
                    const targetObj = jsonData.objects.find(obj => obj._gvid === edge.head);
                    
                    if (sourceObj) sourceName = sourceObj.name || sourceObj._gvid.toString();
                    if (targetObj) targetName = targetObj.name || targetObj._gvid.toString();
                }

                const edgeData = {
                    source: sourceName,
                    target: targetName,
                    id: `${sourceName}-${targetName}`,
                    label: edge.label || '',
                    color: edge.color || '',
                    style: edge.style || 'solid',
                    penwidth: edge.penwidth || '1',
                    points: []
                };

                // Parse spline control points if available
                if (edge.pos) {
                    const splinePoints = this.parseSplineFromJson(edge.pos);
                    edgeData.points = splinePoints;
                }

                edges.push(edgeData);
            }
        }

        console.debug(`Step 5: Extracted ${nodes.length} nodes and ${edges.length} edges`);

        return {
            nodes,
            edges,
            graph: graphInfo
        };
    }

    /**
     * Parse spline control points from JSON pos attribute
     */
    parseSplineFromJson(posString) {
        const points = [];
        try {
            // Remove spline prefixes and parse coordinates
            const cleanPos = posString.replace(/^[se],/, '');
            const coords = cleanPos.split(' ');
            
            for (const coord of coords) {
                if (coord.includes(',')) {
                    const [x, y] = coord.split(',').map(parseFloat);
                    if (!isNaN(x) && !isNaN(y)) {
                        points.push({ x, y });
                    }
                }
            }
        } catch (error) {
            console.warn("Error parsing spline points:", error);
        }
        
        return points;
    }

    /**
     * Transform coordinates from Graphviz to Cytoscape.js format
     */
    transformCoordinates(nodeData, graphHeight) {
        console.debug(`  Transforming node ${nodeData.id}: (${nodeData.x}, ${nodeData.y}) -> (${nodeData.x}, ${graphHeight - nodeData.y})`);

        // Transform coordinates: Graphviz uses bottom-left origin, Cytoscape uses top-left
        return {
            x: nodeData.x,
            y: graphHeight - nodeData.y  // Flip Y coordinate
        };
    }

    /**
     * Calculate distance and weight for control points (ported from Python)
     */
    getDistWeight(sX, sY, tX, tY, pointX, pointY) {
        try {
            let slope;
            if (sX === tX) {
                slope = Infinity;
            } else {
                slope = (sY - tY) / (sX - tX);
            }

            const denom = slope !== Infinity ? Math.sqrt(1 + slope * slope) : 1;
            let d = (pointY - sY + (sX - pointX) * slope) / denom;
            let w = Math.sqrt(Math.pow(pointY - sY, 2) + Math.pow(pointX - sX, 2) - d * d);

            const distAB = Math.sqrt(Math.pow(tX - sX, 2) + Math.pow(tY - sY, 2));
            w = distAB !== 0 ? w / distAB : 0;

            const delta1 = (tX - sX) * (pointY - sY) - (tY - sY) * (pointX - sX) >= 0 ? 1 : -1;
            const delta2 = (tX - sX) * (pointX - sX) + (tY - sY) * (pointY - sY) >= 0 ? 1 : -1;

            d = Math.abs(d) * delta1;
            w = w * delta2;

            // Check for NaN or infinite values
            if (isNaN(d) || !isFinite(d)) {
                d = 0.0;
            }
            if (isNaN(w) || !isFinite(w)) {
                w = 0.5;
            }

            return { d: d.toString(), w: w.toString() };
        } catch (error) {
            console.warn("    Warning: Error calculating control point:", error);
            return { d: "0.0", w: "0.5" };
        }
    }

    /**
     * Convert and validate control points (ported from Python)
     */
    convertControlPoints(dList, wList) {
        try {
            const removeList = [];
            let dTmp = [];
            let wTmp = [];

            // Safely convert to float, handling NaN values
            for (const dVal of dList) {
                try {
                    let dFloat = parseFloat(dVal);
                    if (isNaN(dFloat) || !isFinite(dFloat)) {
                        dFloat = 0.0;
                    }
                    dTmp.push(dFloat);
                } catch (error) {
                    dTmp.push(0.0);
                }
            }

            for (const wVal of wList) {
                try {
                    let wFloat = parseFloat(wVal);
                    if (isNaN(wFloat) || !isFinite(wFloat)) {
                        wFloat = 0.5;
                    }
                    wTmp.push(wFloat);
                } catch (error) {
                    wTmp.push(0.5);
                }
            }

            // Filter out invalid weights
            for (let i = 0; i < wTmp.length; i++) {
                if (wTmp[i] > 1 || wTmp[i] < 0) {
                    removeList.push(wTmp[i]);
                }
            }

            dTmp = dTmp.filter((x, i) => !removeList.includes(wTmp[i]));
            wTmp = wTmp.filter(x => !removeList.includes(x));

            // Check for all zero distances
            const dCheck = dTmp.map(dVal => {
                try {
                    if (isNaN(dVal) || !isFinite(dVal)) {
                        return 0;
                    } else {
                        return Math.round(dVal);
                    }
                } catch (error) {
                    return 0;
                }
            });

            if (new Set(dCheck).size === 1 && dCheck[0] === 0) {
                dTmp = [0.0, 0.0, 0.0];
                wTmp = [0.1, 0.5, 0.9];
            }

            return { dTmp, wTmp };
        } catch (error) {
            console.warn("    Warning: Error in convertControlPoints:", error);
            return { dTmp: [0.0], wTmp: [0.5] };
        }
    }

    /**
     * Calculate control points for edges
     */
    getControlPoints(nodePositions, edges, graphHeight) {
        console.debug("Step 9: Calculating edge control points...");

        for (let i = 0; i < edges.length; i++) {
            const edge = edges[i];
            try {
                const srcId = edge.data.source;
                const tgtId = edge.data.target;

                if (srcId in nodePositions && tgtId in nodePositions) {
                    const src = nodePositions[srcId];
                    const tgt = nodePositions[tgtId];

                    if (src.x !== tgt.x || src.y !== tgt.y) {
                        console.debug(`  Processing edge ${i + 1}/${edges.length}: ${srcId} -> ${tgtId}`);

                        // Use control points from original edge data if available
                        const originalEdge = edge.originalData;
                        if (originalEdge && originalEdge.points && originalEdge.points.length > 2) {
                            // Transform control points and calculate distances/weights
                            const controlPoints = originalEdge.points.slice(1, -1); // Exclude start and end points
                            const distances = [];
                            const weights = [];

                            for (const point of controlPoints) {
                                // Transform Y coordinate
                                const transformedPoint = {
                                    x: point.x,
                                    y: graphHeight - point.y
                                };

                                const { d, w } = this.getDistWeight(
                                    src.x, src.y, tgt.x, tgt.y,
                                    transformedPoint.x, transformedPoint.y
                                );

                                distances.push(d);
                                weights.push(w);
                            }

                            if (distances.length > 0 && weights.length > 0) {
                                edge.data['point-distances'] = distances.join(' ');
                                edge.data['point-weights'] = weights.join(' ');
                                console.debug(`    Added control points: d=[${distances.join(', ')}], w=[${weights.join(', ')}]`);
                            } else {
                                // Ensure we don't have null values
                                edge.data['point-distances'] = '0';
                                edge.data['point-weights'] = '0.5';
                            }
                        } else {
                            // No control points available, use default values
                            edge.data['point-distances'] = '0';
                            edge.data['point-weights'] = '0.5';
                            console.debug(`    No control points for edge ${srcId} -> ${tgtId}, using defaults`);
                        }
                    }
                }
            } catch (error) {
                console.warn(`  Warning: Error processing edge ${i}:`, error);
                // Ensure we don't have null values even on error
                if (edge.data) {
                    edge.data['point-distances'] = '0';
                    edge.data['point-weights'] = '0.5';
                }
                continue;
            }
        }

        return edges;
    }

    /**
     * Build Cytoscape.js elements from graph data
     */
    buildCytoscapeElements(graphData) {
        console.debug("Step 7: Building Cytoscape.js elements...");
        const elements = { nodes: [], edges: [] };

        // Process nodes
        console.debug("Step 8: Processing nodes...");
        for (let i = 0; i < graphData.nodes.length; i++) {
            const node = graphData.nodes[i];
            try {
                // Transform coordinates
                const position = this.transformCoordinates(node, graphData.graph.height);
                
                // Store position for edge calculations
                this.nodePositions[node.id] = position;

                // Create Cytoscape node
                const cytoNode = {
                    data: {
                        id: node.id,
                        label: node.label || node.id
                    },
                    position: position
                };

                // Add styling attributes
                const styleAttrs = ['color', 'fillcolor', 'fontcolor', 'shape'];
                for (const attr of styleAttrs) {
                    if (node[attr]) {
                        cytoNode.data[attr] = node[attr];
                    }
                }

                elements.nodes.push(cytoNode);
            } catch (error) {
                console.warn(`  Warning: Error processing node ${i}:`, error);
                continue;
            }
        }

        // Process edges
        console.debug("Step 8b: Processing edges...");
        const edgesData = [];
        for (let i = 0; i < graphData.edges.length; i++) {
            const edge = graphData.edges[i];
            try {
                const edgeData = {
                    data: {
                        id: edge.id,
                        source: edge.source,
                        target: edge.target
                    },
                    originalData: edge // Store original edge data for control point calculation
                };

                // Add edge attributes
                const edgeAttrs = ['label', 'color', 'style', 'penwidth'];
                for (const attr of edgeAttrs) {
                    if (edge[attr]) {
                        edgeData.data[attr] = edge[attr];
                    }
                }

                edgesData.push(edgeData);
            } catch (error) {
                console.warn(`  Warning: Error processing edge ${i}:`, error);
                continue;
            }
        }

        // Calculate control points for edges
        try {
            const processedEdges = this.getControlPoints(this.nodePositions, edgesData, graphData.graph.height);
            elements.edges = processedEdges;
        } catch (error) {
            console.warn("Warning: Error calculating control points, using simple edges:", error);
            elements.edges = edgesData;
        }   

        console.debug(`Step 10: Conversion complete! Created ${elements.nodes.length} nodes and ${elements.edges.length} edges`);
        return elements;
    }

    /**
     * Main conversion method
     */
    async convertToCytoscape(dotString) {
        try {
            console.log("=== Starting DOT to Cytoscape.js Conversion ===");

            // Ensure Graphviz is initialized
            if (!this.isInitialized) {
                await this.initialize();
            }

            console.debug(`DOT file content length: ${dotString.length} characters`);

            // Convert and parse using JSON format
            const jsonGraphData = this.renderDotToJson(dotString);
            const graphData = this.parseJsonGraphData(jsonGraphData);

            // Build Cytoscape elements
            const elements = this.buildCytoscapeElements(graphData);

            console.debug("Final elements:", elements);

            console.log("=== Conversion Successful! ===");
            return elements;
        } catch (error) {
            console.error("=== Conversion Failed ===");
            console.error("Error:", error.message);
            console.error(error);
            throw new Error(`Error converting DOT file: ${error.message}`);
        }
    }
}

/**
 * Convenience function to convert a DOT string to Cytoscape.js format
 */
export async function convertDotString(dotString) {
    if (!dotString) {
        throw new Error("DOT string must be provided.");
    }

    const converter = new DotToCytoscapeConverter();
    return await converter.convertToCytoscape(dotString);
}